/**
 * MicroAdapter - Slim platform adapter for v6 thin client.
 *
 * Only handles what requires browser APIs:
 * - Page context detection (page type, product ID)
 * - Cart event listening
 * - CTA actions (addToCart, applyCoupon)
 * - Live cart data
 *
 * Everything else (user data, product data, variable resolution,
 * intelligence) is handled server-side via /api/sdk/init.
 */

export interface PageContext {
  url: string;
  path: string;
  pageType: 'product' | 'collection' | 'cart' | 'checkout' | 'order_confirmation' | 'home' | 'search' | 'other';
  productId?: string;
  collectionId?: string;
  collectionName?: string;
  searchQuery?: string;
  /** Order code extracted from order confirmation URL */
  orderCode?: string;
}

export interface LiveCart {
  total: number;
  itemCount: number;
  lastItemName: string | null;
  currency: string;
}

export const EMPTY_CART: LiveCart = {
  total: 0,
  itemCount: 0,
  lastItemName: null,
  currency: 'SAR',
};

export interface MicroAdapter {
  /** Detect platform availability */
  detect(): { platform: 'salla' | 'zid' | 'website' | 'unknown'; available: boolean };

  /** Collect page context — called once at init, sent to server */
  getPageContext(): PageContext;

  /** Get current cart state */
  getCart(): Promise<LiveCart>;

  /** Listen for cart changes */
  onCartChange(handler: (cart: LiveCart) => void): void;

  /** Listen for checkout button clicks */
  onCheckoutIntent(handler: () => void): void;

  /** Listen for user login */
  onUserLogin(handler: (user: { id: string; email?: string }) => void): void;

  /** CTA: Add product to cart */
  addToCart(productId: string, quantity?: number): Promise<boolean>;

  /** CTA: Apply coupon code */
  applyCoupon(code: string): Promise<boolean>;

  /** Cleanup all listeners */
  destroy(): void;
}
